// -----------------------------------------------------------------------------
// UnclaimedProperty.jsx — canonical page for /texas-unclaimed-property-probate
//
// Secondary probate service page for Texas families who discover a deceased
// relative's property on ClaimItTexas.gov and need help establishing who is
// legally entitled to claim it. Positioned as one useful part of the firm's
// probate practice, NOT the primary service.
//
// Compliance guardrails (strict):
//   • No client names or specific-client facts
//   • No flat fees; no recovery promises
//   • Do NOT say every claim requires probate
//   • Do NOT say muniment is automatically available after four years
//   • Do NOT say asset discovery automatically excuses delay
//   • Do NOT say a court will grant an application
//   • Do NOT imply Comptroller affiliation
//   • Williamson County Court at Law No. 4 published guidance: IN-PERSON
//     hearings (never describe as virtual)
//   • "nearly 30 years" experience wording
//   • Full identity: "LONE STAR ★ COUNSEL, a dba of Pratt Law Group, PLLC"
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Star, ExternalLink } from "lucide-react";

const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";
const CANONICAL = "/texas-unclaimed-property-probate";
const CLAIMIT_URL = "https://claimittexas.gov/";

// Possible legal paths — described in plain English, no promises about
// availability in any specific case.
const POSSIBLE_PATHS = [
  ["A claim supported by existing probate documents", "If the estate has already been administered and there is a will admitted to probate, letters testamentary, or another court order that identifies the person entitled to the property, a claim can often be filed with the Comptroller supported by those existing documents. The Comptroller determines its own claim requirements; a probate attorney can help assemble the file."],
  ["A qualifying claim under an unprobated will", "In defined circumstances, the Comptroller may accept a claim supported by an unprobated will together with additional documentation. Whether the specific facts qualify depends on the will, the property, the identity of the claimant, applicable Comptroller policy, and Texas law. This path is not available in every case."],
  ["Probate of a will as a muniment of title (when legally available)", "When the estate has a valid will, no unpaid debts other than debts secured by real property, and the requirements of the Texas Estates Code are met, the will may be admitted to probate as a muniment of title — the document that transfers title — without appointing a personal representative. This is a fact-specific determination in every case; it is not automatically available and cannot be assumed."],
  ["A determination of heirship (when there is no will)", "When the deceased person did not leave a will, a judicial determination of heirship may be needed to establish, on the court record, who the heirs are under Texas intestacy law. The proceeding involves an attorney ad litem appointed by the court and defined statutory notices. The order of heirship can then support a claim with the Comptroller and clear title to other property."],
];

// What families should gather before calling — helps triage the call.
const GATHER_LIST = [
  "The ClaimItTexas property or claim reference (the property ID or claim number shown on the Comptroller site).",
  "The deceased person's full legal name (and any prior names), date of birth, and date of death.",
  "Any will \u2014 including a copy or the location of the original.",
  "Any prior probate orders \u2014 letters testamentary, order admitting the will to probate, order determining heirship, or muniment order.",
  "The death certificate.",
  "The names, addresses, and relationships of the potential beneficiaries or heirs.",
];

// Priority FAQs — the actual questions families ask.
const PRIORITY_FAQ = [
  ["What is Texas unclaimed property, and where does it come from?",
   "Texas unclaimed property is money or other property held by financial institutions, insurance companies, employers, courts, and other entities that has gone dormant \u2014 typically because the owner cannot be located or contacted. Under Texas law, these entities transfer dormant property to the Texas Comptroller of Public Accounts, which holds it until the rightful owner or the owner's heirs come forward. The Comptroller publishes a public database of unclaimed property at ClaimItTexas.gov."],
  ["I found property in a deceased relative's name. What do I do first?",
   "First, gather the ClaimItTexas property or claim reference and the basic facts about the deceased person and any potential beneficiaries or heirs (see the checklist above). Do not send Social Security numbers or copies of sensitive documents through a general website contact form. Contact a Texas probate attorney to discuss the specific facts and the Comptroller's current claim requirements. The right path depends on the will, the beneficiaries, the type of property, whether there was any prior probate, any debts, and how long it has been since the death."],
  ["Does every unclaimed-property claim require probate?",
   "No. Some claims can be filed with the Comptroller using documents from a prior probate proceeding, and in defined circumstances the Comptroller may accept an unprobated will together with additional documentation. Some claims will require a new probate proceeding \u2014 a muniment of title, a full administration, or a determination of heirship \u2014 to establish who is legally entitled to receive the property. The correct path is fact-specific."],
  ["Is there a fee to search for or claim my property with the Comptroller?",
   "No. Searching the Texas Comptroller's ClaimItTexas.gov database and filing a claim with the Comptroller is free. Lone Star Counsel is not affiliated with the Texas Comptroller and does not charge for what the state provides free. The firm's fees are for legal assessment of the path, probate representation when a proceeding is required, and any separately agreed claim assistance \u2014 discussed and documented in a written engagement before any work begins."],
  ["What if the person has been deceased for more than four years?",
   "Texas probate available more than four years after death is subject to statutory conditions that require case-specific legal review. A muniment of title is not automatically available after four years; discovering an asset does not automatically excuse the delay; and no attorney can assure any client that a court will grant a late-filed application. Whether a late probate proceeding is available depends on the facts, the specific court, and the applicable statute. This category of case requires individual review before any commitment can be made."],
  ["How long does the whole process take?",
   "Timelines vary widely with the type of proceeding, the specific court, the completeness of the documentation, and the Comptroller's claim-processing queue. A straightforward claim supported by existing probate documents may resolve in a matter of weeks. A new muniment of title, or a determination of heirship, plus the Comptroller's claim process, can take several months. Contested matters take longer. No specific timeline can be guaranteed for a specific matter."],
  ["Will the firm work anywhere in Texas?",
   "Yes. The firm represents Texas families statewide. Court-appearance requirements vary by county \u2014 some counties permit remote appearances for certain hearings, others require in-person appearances. Williamson County Court at Law No. 4's published probate guidance, for example, requires in-person hearings. County-specific requirements are addressed in the initial assessment."],
  ["How do I keep my sensitive information safe when contacting the firm?",
   "Please do not enter Social Security numbers, financial account numbers, tax IDs, or copies of sensitive documents into the general website contact form. Use the form to request a conference and share only what is needed to schedule. Sensitive documents are exchanged through a secure channel established once the engagement is confirmed."],
];

// Contextual cross-links.
const CONTEXTUAL_LINKS = [
  { label: "Texas Probate Attorney", desc: "The main probate hub \u2014 independent administration, dependent administration, muniment of title, small estate affidavit, heirship determination, and ancillary probate.", href: "/texas-probate-attorney/" },
  { label: "Muniment of Title", desc: "The streamlined Texas proceeding sometimes used to clear title to a specific asset when the strict statutory conditions are met.", href: "/texas-probate-attorney/#types" },
  { label: "Estate Planning", desc: "A coordinated plan \u2014 wills, revocable trusts, powers of attorney, and beneficiary designations \u2014 that helps prevent property from becoming unclaimed in the first place.", href: "/texas-estate-planning-attorney/" },
  { label: "Trust Administration", desc: "When the deceased had a trust, coordinated trust administration reaches property that never enters the probate court.", href: "/trusts/trust-administration" },
];

function FaqItem({ q, a, i, onOpen }) {
  const [open, setOpen] = useState(i === 0);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };
  return (
    <div className="hairline-top py-6" data-testid={`unclaimed-faq-${i}`}>
      <button
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex justify-between items-start gap-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass"
      >
        <h3 className="font-display text-xl md:text-2xl text-navy leading-tight">{q}</h3>
        <ChevronDown
          size={22}
          className={`shrink-0 text-brass transition-transform mt-1 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`${open ? "block" : "hidden"} mt-4 text-slate2 font-serif text-base leading-relaxed`}>
        {a}
      </div>
    </div>
  );
}

export default function UnclaimedProperty() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-unclaimed-property-probate",
      page_title: "Texas Unclaimed Property Probate Attorney",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);

  return (
    <div className="bg-ivory" data-testid="unclaimed-property-page">
      <SEO
        title="Texas Unclaimed Property Probate Attorney | Claim a Deceased Relative's Property"
        description="Found property in a deceased Texas relative's name on ClaimItTexas.gov? Lone Star Counsel helps families determine the appropriate legal path \u2014 existing probate, unprobated will, muniment of title, or heirship determination \u2014 and handles the probate proceeding when needed."
        keywords="Texas unclaimed property attorney, ClaimItTexas probate, muniment of title unclaimed property Texas, deceased relative unclaimed property Texas, Texas Comptroller unclaimed property claim, heirship determination Texas, probate attorney Texas unclaimed"
        path={CANONICAL}
        schema={[
          legalServiceSchema({
            name: "Texas Unclaimed Property Probate Attorney",
            description: "Texas probate counsel for families who have located a deceased relative's property on ClaimItTexas.gov and need to establish who is legally entitled to claim it \u2014 existing probate, unprobated will, muniment of title, or heirship determination.",
            url: `https://lonestarcounsel.com${CANONICAL}`,
          }),
          attorneySchema(),
          faqPageSchema(PRIORITY_FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Texas Probate Attorney", path: "/texas-probate-attorney/" },
            { name: "Texas Unclaimed Property Probate Attorney", path: CANONICAL },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="unclaimed-hero">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16">
          <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            <Link to="/texas-probate-attorney/" className="hover:text-oxblood transition-colors">
              Texas Probate
            </Link>
            <span className="text-navy/40 mx-3">/</span>
            <span className="text-navy/60">Unclaimed Property Claims</span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy" data-testid="unclaimed-h1">
            Texas Unclaimed Property Probate<br />
            <span className="italic text-oxblood">Attorney.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate2 font-serif leading-relaxed" data-testid="unclaimed-direct-answer">
            Found money or other property in a deceased family member&rsquo;s name on{" "}
            <a
              href={CLAIMIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-oxblood underline decoration-brass/40 hover:decoration-oxblood underline-offset-4"
              data-testid="unclaimed-hero-claimit-link"
            >
              ClaimItTexas.gov
            </a>
            ? A will, court order, or other proof of inheritance may be needed before the Texas
            Comptroller can release it. Lone Star &#9733; Counsel helps Texas families determine
            the appropriate path and, when necessary, handle the probate proceeding.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link to="/intake" className="btn-primary" data-testid="unclaimed-hero-cta-primary">
              Request an Initial Assessment
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/50 hover:border-oxblood pb-1"
              data-testid="unclaimed-hero-cta-phone"
            >
              <Phone size={11} className="inline mr-2" />
              or call {PHONE}
            </a>
          </div>
          <div className="mt-8 max-w-2xl">
            <AttorneyReviewNote reviewed="February 2026" compact />
          </div>
        </div>
      </section>

      {/* PATHS */}
      <section id="paths" className="bg-linen hairline-top py-20 md:py-28" data-testid="unclaimed-paths">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Possible legal paths</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            The path depends <span className="italic text-oxblood">on the facts.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            There is no single path to claiming a deceased Texan&rsquo;s unclaimed property.
            The right one depends on the will, the beneficiaries, the type of property, any
            prior probate proceeding, the estate&rsquo;s debts, and how long it has been since
            the death. In many cases, one of the following four paths applies.
          </p>
          <ul className="mt-10 space-y-4" data-testid="unclaimed-paths-list">
            {POSSIBLE_PATHS.map(([title, desc], i) => (
              <li key={i} className="hairline-top pt-5" data-testid={`unclaimed-path-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-brass" aria-hidden>&#9733;</span>
                  <h3 className="font-display text-lg md:text-xl text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-navy/80 leading-relaxed font-serif">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MORE THAN FOUR YEARS — carefully worded prominent section */}
      <section id="four-years" className="bg-ivory py-20 md:py-28" data-testid="unclaimed-four-years">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="kicker">A category that requires individual review</div>
              <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-[1.05]">
                What if the person died <span className="italic text-oxblood">more than four years ago?</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-navy font-serif leading-relaxed">
                Texas probate available more than four years after death is subject to
                statutory conditions that require case-specific legal review.
              </p>
              <ul className="mt-6 space-y-3 text-navy/85 font-serif leading-relaxed">
                <li className="hairline-top pt-3 flex gap-3">
                  <span className="text-brass mt-2 shrink-0" aria-hidden>&#9744;</span>
                  <span>A muniment of title is <em>not</em> automatically available after four years.</span>
                </li>
                <li className="hairline-top pt-3 flex gap-3">
                  <span className="text-brass mt-2 shrink-0" aria-hidden>&#9744;</span>
                  <span>Discovering an asset does <em>not</em> automatically excuse the delay.</span>
                </li>
                <li className="hairline-top pt-3 flex gap-3">
                  <span className="text-brass mt-2 shrink-0" aria-hidden>&#9744;</span>
                  <span>No attorney can assure any client that a court will grant a late-filed application.</span>
                </li>
              </ul>
              <p className="mt-6 text-navy/85 font-serif leading-relaxed">
                Whether a late probate proceeding is available depends on the specific facts,
                the specific court, and the applicable statute. This category of case requires
                individual review before any commitment can be made.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO GATHER */}
      <section id="gather" className="bg-linen hairline-top py-20 md:py-28" data-testid="unclaimed-gather">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Before you call</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            What to gather <span className="italic text-oxblood">before calling.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            The initial assessment goes faster when the following are on hand. Do not enter
            Social Security numbers or upload sensitive documents through a general website
            contact form.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3" data-testid="unclaimed-gather-list">
            {GATHER_LIST.map((item, i) => (
              <li key={i} className="hairline-top pt-4 flex gap-3" data-testid={`unclaimed-gather-${i}`}>
                <Star size={9} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                <span className="text-navy/85 leading-relaxed font-serif">{item}</span>
              </li>
            ))}
          </ul>

          {/* Security callout */}
          <div className="mt-12 p-6 border-l-4 border-oxblood bg-ivory" data-testid="unclaimed-security-note">
            <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium">
              Please note
            </div>
            <p className="mt-3 text-navy/85 font-serif leading-relaxed">
              Please do <strong>not</strong> enter Social Security numbers, financial account
              numbers, or copies of sensitive documents into the general contact form. Use the
              form to request a conference and share only what is needed to schedule. Sensitive
              documents are exchanged through a secure channel established once the engagement
              is confirmed.
            </p>
          </div>
        </div>
      </section>

      {/* CLAIMITTEXAS + FEES */}
      <section id="fees" className="bg-ivory py-20 md:py-28" data-testid="unclaimed-fees">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <div className="kicker">Where to search</div>
              <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-[1.05]">
                Search the official <span className="italic text-oxblood">Texas Comptroller database.</span>
              </h2>
              <p className="mt-6 text-navy font-serif leading-relaxed">
                The Texas Comptroller of Public Accounts publishes the official database of
                unclaimed property at ClaimItTexas.gov. Searching for and filing a claim with
                the Comptroller is <strong>free</strong>. Lone Star Counsel is not affiliated
                with the Texas Comptroller.
              </p>
              <a
                href={CLAIMIT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 btn-primary"
                data-testid="unclaimed-claimit-cta"
              >
                Search ClaimItTexas.gov
                <ExternalLink size={14} aria-hidden />
              </a>
              <p className="mt-4 text-xs text-navy/60 font-serif">
                Opens the official Texas Comptroller site in a new tab.
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="kicker">What the firm charges for</div>
              <h3 className="mt-6 font-display text-2xl md:text-3xl text-navy leading-tight">
                A written engagement. <span className="italic text-oxblood">No flat promises.</span>
              </h3>
              <p className="mt-6 text-navy/85 font-serif leading-relaxed">
                The firm&rsquo;s fees are for legal assessment of the appropriate path, probate
                representation when a proceeding is required, and any separately agreed claim
                assistance. Fees, scope, and the applicable retainer are discussed and
                documented in a written engagement agreement before any work begins. The firm
                does not publish a flat fee for this category of matter, does not promise
                recovery, and does not suggest that every claim requires probate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WILLIAMSON COUNTY CALLOUT */}
      <section className="bg-linen hairline-top py-16 md:py-20" data-testid="unclaimed-williamson-callout">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="p-8 md:p-10 border-t-2 border-brass bg-ivory">
            <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
              County note
            </div>
            <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-tight">
              Williamson County Court at Law No. 4.
            </h3>
            <p className="mt-4 text-navy/85 font-serif leading-relaxed">
              Court-appearance requirements vary by Texas county. Williamson County Court at
              Law No. 4&rsquo;s published probate guidance requires in-person hearings. Other
              counties may permit remote appearances for certain hearings. County-specific
              requirements are addressed in the initial assessment. The firm represents Texas
              families statewide.
            </p>
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section id="faqs" className="bg-ivory py-20 md:py-28" data-testid="unclaimed-faq">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked Questions</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-navy leading-[1.02]">
            Practical answers <span className="italic text-oxblood">for Texas families.</span>
          </h2>
          <p className="mt-4 max-w-3xl text-navy/75 font-serif leading-relaxed">
            General information only &mdash; not legal advice on your specific matter. Contact
            the firm to discuss the facts of a particular case.
          </p>
          <div className="mt-10" data-testid="unclaimed-priority-faq">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <FaqItem
                key={i}
                q={q}
                a={a}
                i={i}
                onOpen={() =>
                  trackPriorityFaqOpen({
                    page_slug: "texas-unclaimed-property-probate",
                    faq_position: i + 1,
                    faq_question: q,
                  })
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED / CROSS-LINKS */}
      <section id="related" className="bg-linen hairline-top py-16 md:py-20" data-testid="unclaimed-related">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related counsel</div>
          <h2 className="mt-6 font-display text-2xl md:text-3xl text-navy leading-[1.05]">
            Part of a coordinated <span className="italic text-oxblood">probate practice.</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="unclaimed-related-list">
            {CONTEXTUAL_LINKS.map((c, i) => (
              <Link
                key={i}
                to={c.href}
                className="group hairline-top pt-5 block hover:bg-ivory/60 transition-colors -mx-3 px-3"
                data-testid={`unclaimed-related-${i}`}
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                  {c.label}
                </div>
                <h3 className="mt-3 font-display text-base md:text-lg text-navy leading-snug group-hover:text-oxblood transition-colors">
                  {c.desc}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section id="contact" className="panel-navy grain grain-dark py-20 md:py-28 relative overflow-hidden" data-testid="unclaimed-cta">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" aria-hidden />
          <div className="mt-8 kicker kicker-ivory">A message from the firm</div>
          <h2 className="mt-4 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
            Request an <span className="italic font-cormorant text-brass">Initial Assessment.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-ivory/85 font-serif text-lg leading-relaxed">
            Every unclaimed-property claim is different. The initial assessment reviews the
            facts, identifies the appropriate legal path (or paths), and produces a written
            engagement scope before any legal work begins. Nearly 30 years of Texas business,
            estate, and probate counsel.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <Link to="/intake" className="btn-brass" data-testid="unclaimed-cta-primary">
              Request a Confidential Conference
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
              data-testid="unclaimed-cta-phone"
            >
              <Phone size={11} className="inline mr-2" />
              or call {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-navy text-ivory/70 py-10 text-xs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-serif leading-relaxed">
          <strong className="text-brass tracking-widest uppercase text-[10px]">Disclaimer.</strong>{" "}
          The information on this page is for general informational purposes only and is not
          legal, tax, accounting, or financial advice. Reading this page or contacting the firm
          does not create an attorney-client relationship. An attorney-client relationship is
          established only through a written engagement agreement signed by the client and the
          firm. Lone Star Counsel is not affiliated with the Texas Comptroller of Public
          Accounts. Searching and filing an unclaimed-property claim with the Comptroller is
          free at ClaimItTexas.gov. Past results do not guarantee future outcomes. Each matter
          depends on its specific facts, the applicable Texas Estates Code procedure, the
          Texas Comptroller&rsquo;s current claim requirements, and the court where any
          proceeding is filed. LONE STAR &#9733; COUNSEL, a dba of Pratt Law Group, PLLC.
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
